import { sleep } from "effection";
import { nanoid } from "nanoid";
import { createSelector } from "reselect";
import { createAssign } from "robodux";
import { call, delay, leading, put, select, spawn } from "saga-query";
import { call as callStarfx, PERSIST_LOADER_ID } from "starfx";

import { isOk } from "@app/state/helpers";
import { TWhoami } from "@app/statefx/schema/app.def";
import { initGxWorker } from "@app/statefx/workers";
import { persistor } from "@app/statefx/xstore";
import { sfxUpdateWhoami, updateAppData } from "@app/statefx/xThunks/app";
import { runBootSequence } from "@app/statefx/xThunks/ds";
import { cleanupSfx, sfxCloneSagaLogin } from "@app/statefx/xThunks/login";
import { loadUiState } from "@app/statefx/xThunks/ui-persist";

import { inApi, inThunks } from "../apis/api";
import { RootState } from "../rootState";
import { selectAllGroupsList } from "../units/groups";
import { appNotify } from "../units/notify";
import {
  clearInsiderSeed,
  selectUiDrawerInsider,
} from "../units/ui/maindrawer";
import { cleanupProfiles } from "./profilesApi";
import {
  cleanupListUi,
  getListDefinition,
  lxInvoiceCriteria,
  lxMyWorkCriteria,
} from "./ui/uiList";

import type { TInsLoginData } from "@app/types";

const INS_LOGIN = "insLogin";
export const insLogin = createAssign<TInsLoginData>({
  name: INS_LOGIN,
  initialState: {
    AMAXAVOID: -1,
    INSIDER: "",
    DISPLAY_NAME: "",
    DREPT_SUPREM: 0,
    DREPT_FACTURARE: 0,
    LOGINCTX: "",
  },
});
const sendNotification = function* (message: string, duration = 5000) {
  yield* spawn(appNotify.run, appNotify({ type: "error", message, duration }));
};

export const inLogin = inApi.post<string>(
  "inapi/insider/login",
  { saga: leading },
  function* (ctx, next) {
    process.env.NODE_ENV === "development" && console.log("[dev.] inLogin");
    yield* put(insLogin.actions.reset());
    yield* put(cleanupListUi());
    //todo: cleanup other caches ... but keep this one

    // const endpoit = yield* select(endpoint,`/inapi/insider/login`);
    const selectedClientPerseed = yield* select(selectUiDrawerInsider);
    const newSessionId = nanoid();
    //secure channel
    ctx.request = ctx.req({
      method: "POST",
      url: `/login`,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        credentials: "include",
      },
      body: JSON.stringify({
        insider: selectedClientPerseed,
        geml: ctx.payload,
        micro: newSessionId,
      }),
    });
    yield* next();

    const { data } = ctx.json;
    if (!isOk(data)) {
      yield* sendNotification(data.error?.message || "Eroare la logare", 3000);
      return;
    }
    const {
      AMA_ID,
      PRENUME,
      NUME,
      DREPT_SUPREM,
      DREPT_FACTURARE,
      INSIDER,
      LOGINCTX,
    } = data.value as any; //..

    const stateObj = {
      AMAXAVOID: AMA_ID,
      INSIDER: INSIDER,
      DISPLAY_NAME: `${PRENUME} ${NUME}`,
      DREPT_SUPREM: DREPT_SUPREM,
      DREPT_FACTURARE: DREPT_FACTURARE,
      LOGINCTX: LOGINCTX,
    };
    yield* put(insLogin.actions.set(stateObj));

    yield* spawn(cleanupOtherCache, LOGINCTX);
    yield* put(restoreTrackState());
    yield* put(getListDefinition());
    yield* call(lxMyWorkCriteria.run, lxMyWorkCriteria());
    yield* call(lxInvoiceCriteria.run, lxInvoiceCriteria());
    // inject data into the starfx store //
    const _currentGroup = yield* select(selectAllGroupsList);
    const gid = LOGINCTX.split("/")[0];
    const currentGroup = _currentGroup[gid];
    const EML = (ctx.payload || "").split("/")?.[1] || "";
    const who = {
      Prenume: PRENUME,
      PrenumeNume: `${PRENUME} ${NUME}`,
      AmaId: AMA_ID,
      DreptSuprem: DREPT_SUPREM,
      DreptFacturare: DREPT_FACTURARE,
      AppIsPersonalKarma: false,
      LOGINCTX: LOGINCTX,
      wai_host: "",
      wai_path: "/",
      wai_database: INSIDER,
    } as TWhoami;

    // jgap startup | insider startup | initialise | insider login | insider boot

    const indexDbName = `w.${who.AppIsPersonalKarma ? "K" : "J"}${who.AmaId}.${
      who.wai_database
    }`; //w.J16.SWCRE//

    yield* window.fx.run(function* () {
      yield* callStarfx(() => initGxWorker(indexDbName));
      //KEEP THIS
      yield* window.fx.dispatch([
        cleanupSfx(), //appJobs reset //
        sfxCloneSagaLogin({ ...stateObj, EML: EML }),
        updateAppData({ idb: INSIDER }),
        sfxUpdateWhoami(who),
        /// LAUNCH STARFX TENANT BOOTUP
        runBootSequence(),
        ///
      ]);
    });
  }
);

export const insLogout = inApi.post(
  "inapi/insider/logout",
  { saga: leading },
  function* (ctx, next) {
    const currentLogin = yield* select(selectInsLogin);
    if (!currentLogin.LOGINCTX) {
      return;
    }
    const currentLoginCtx = currentLogin.LOGINCTX;
    //secure channel
    ctx.request = ctx.req({
      method: "POST",
      url: `/logout`,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        credentials: "include",
      },
      body: JSON.stringify({ u: "any" }),
    });
    //tear it up
    yield* put(insLogin.actions.reset());
    yield* put(clearInsiderSeed());
    yield* put(cleanupProfiles());
    yield* put(cleanupListUi());
    // yield* put(cleanupInvoiceData());
    // don't. we will cleanup on next login, if needed
    // yield* call(cleanupInsiderCache, currentLoginCtx);
  }
);

export const setInsLogin = inThunks.create<{ username: string }>(
  "thunks/insider/setInsLogin",
  function* (ctx, next) {
    const crtstate = yield* select(selectInsLogin);
    yield* put(insLogin.actions.set({ ...crtstate, ...ctx.payload }));
  }
);

//selectors
export const selectInsLogin = (state: RootState) => state[INS_LOGIN];
export const selectInsLoginUsername = createSelector(
  selectInsLogin,
  (insLogin): TInsLoginData => insLogin
);

export const select_api_login_amaid = createSelector(
  selectInsLogin,
  (insLogin) => insLogin.AMAXAVOID
);

export const selectCurrentLoginContext = createSelector(
  selectInsLogin,
  (insLogin) => insLogin.LOGINCTX
);

//the actual cleanup based on a hsum key
export function* cleanupInsiderCache(loginCtxKey: string) {
  if (!loginCtxKey) {
    return;
  }
  const getSagaCache = (state: RootState) => state?.["@@saga-query/data"] || {};
  const cacheMan = yield* select(getSagaCache);
  if (!cacheMan) {
    return;
  }
  const fil = Object.keys(cacheMan).filter((key) =>
    key.startsWith(loginCtxKey)
  );
  yield* delay(300);
  yield* put({ type: "@@saga-query/data/remove", payload: fil });
}

export function* cleanupOtherCache(loginCtxKey: string) {
  if (!loginCtxKey) {
    return;
  }
  const getSagaCache = (state: RootState) => state?.["@@saga-query/data"] || {};
  const cacheMan = yield* select(getSagaCache);
  if (!cacheMan) {
    return;
  }
  const [_, myUnique] = loginCtxKey.split("/");
  const fil = Object.keys(cacheMan).filter(
    (key) => !key.startsWith(loginCtxKey) && key.includes(`/${myUnique}/`)
  );
  yield* delay(300);
  if (fil.length > 0) {
    yield* put({ type: "@@saga-query/data/remove", payload: fil });
  }
}

/* -------------------------------------------------------------------------- */

export const restoreTrackState = inApi.get(
  "inapi/insider/trackTreeState",
  { saga: leading },
  function* (ctx, next) {
    const thisLoginCtx = yield* select(selectCurrentLoginContext);
    ctx.cache = true;
    ctx.key = `${thisLoginCtx}/trackState`;
    ctx.request = ctx.req({
      method: "GET",
      url: "/assets/trackState",
    });
    yield* next();
    const { data, ok } = ctx.json;
    if (ok) {
      if (isOk(data)) {
        //
        if (data) {
          if (!data) {
            // not intialized
            return;
          }
          process.env.NODE_ENV === "development" &&
            console.log("[dev.] restoring track state", data);

          yield* put({
            type: "/inapi/insider/trackStatePreseved",
            payload: data,
          });
          return;
        }
      }
    }
    console.log("restoring track state failed", data);
  }
);
